/**
 * Activity feed & notification contracts — DESIGN.md §6.10, §7.5, §11.1.
 *
 * The API returns audit events as data, not as prose: the payload carries the
 * ids and the resolved display names, and the client renders the sentence.
 * That keeps the feed translatable, keeps the wire small, and means a
 * rendering change never requires a data migration.
 */
import { z } from 'zod';
import { ACTOR_TYPES, ENTITY_TYPES, EVENT_TYPES, NOTIFICATION_KINDS } from '../enums.js';
import { paginationSchema, uuidSchema } from './common.js';
import { userRefSchema } from './users.js';
/**
 * Who did it. `user` carries the person; `agent` carries the person *and* the
 * client name so the feed can render "Brad · via Claude Code" (§12.5);
 * `system` and `automation` carry no person at all (§6.10).
 */
export const actorSchema = z.object({
    type: z.enum(ACTOR_TYPES),
    user: userRefSchema.nullable(),
    /** MCP client name for agents, rule name for automations. */
    via: z.string().nullable(),
});
export const activityEventSchema = z.object({
    id: z.number().int().positive(),
    entityType: z.enum(ENTITY_TYPES),
    entityId: uuidSchema,
    taskId: uuidSchema.nullable(),
    eventType: z.enum(EVENT_TYPES),
    payload: z.record(z.unknown()),
    actor: actorSchema,
    createdAt: z.string(),
});
export const listActivityQuerySchema = paginationSchema;
/**
 * The admin audit query — §6.10's stream, filtered.
 *
 * Every filter is optional and they intersect. `eventType` and `entityType`
 * arrive as comma-separated lists because a querystring is where this lives
 * and an admin filtering to "everything about people" wants several at once.
 * The bounds are inclusive-from / exclusive-to, so a single day is
 * `from=2026-08-27&to=2026-08-28` with no arithmetic at the call site.
 */
export const auditQuerySchema = paginationSchema.extend({
    actorId: uuidSchema.optional(),
    /**
     * #76 — "The Audit Log should be able to identify 'Claude' or agents as well
     * in the actions it takes on behalf of itself or others."
     *
     * An agent's write is audited against the person who authorised it (§12.5),
     * which is right — it is how a token cannot launder an action away from its
     * owner — but it also means `actorId` alone cannot tell "Brad changed this"
     * from "Brad's agent changed this". This is the axis that can: the same
     * person, filtered by *how* they were acting.
     */
    actorType: z
        .string()
        .optional()
        .transform((value) => splitList(value))
        .pipe(z.array(z.enum(ACTOR_TYPES)).max(ACTOR_TYPES.length)),
    entityId: uuidSchema.optional(),
    entityType: z
        .string()
        .optional()
        .transform((value) => splitList(value))
        .pipe(z.array(z.enum(ENTITY_TYPES)).max(ENTITY_TYPES.length)),
    eventType: z
        .string()
        .optional()
        .transform((value) => splitList(value))
        .pipe(z.array(z.enum(EVENT_TYPES)).max(EVENT_TYPES.length)),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
function splitList(value) {
    if (!value)
        return [];
    return [
        ...new Set(value
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean)),
    ];
}
export const notificationSchema = z.object({
    id: z.number().int().positive(),
    kind: z.enum(NOTIFICATION_KINDS),
    event: activityEventSchema,
    /** Denormalised so the Inbox can group by card without a second call. */
    task: z
        .object({
        id: uuidSchema,
        number: z.number().int().positive(),
        title: z.string(),
    })
        .nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
export const listNotificationsQuerySchema = paginationSchema.extend({
    unreadOnly: z
        .enum(['true', 'false'])
        .default('false')
        .transform((v) => v === 'true'),
});
/**
 * Three ways to say what to mark read — card #52.
 *
 * `taskId` is the one Ben's card asks for: "Same thing happens when I read it
 * via clicking the card itself and not via the inbox." Opening a card cannot
 * name the notification ids — the Inbox may never have been loaded, and a card
 * can have more of them than one page holds — so the card names *itself* and
 * the server works out which rows that is.
 */
export const markReadRequestSchema = z
    .object({
    ids: z.array(z.number().int().positive()).max(500).optional(),
    /** Every unread notification about this card. */
    taskId: uuidSchema.optional(),
    all: z.boolean().default(false),
})
    .strict()
    .refine((v) => v.all || v.taskId !== undefined || (v.ids?.length ?? 0) > 0, {
    message: 'Pass ids, taskId or all:true',
});
export const unreadCountSchema = z.object({ unread: z.number().int().nonnegative() });
//# sourceMappingURL=activity.js.map